import { Trophy, CalendarDays } from "lucide-react";
import { cn } from "../../../lib/utils";
import type { PeriodStats } from "../types";
import { useLocale } from "../../../context/LanguageContext";

interface ChallengeEventSummaryProps {
  current: PeriodStats | undefined;
  loading?: boolean;
}

export function ChallengeEventSummary({
  current,
  loading = false,
}: ChallengeEventSummaryProps) {
  const { t } = useLocale();

  if (loading) {
    return (
      <div className="grid gap-4 sm:grid-cols-2">
        <SummarySkeleton />
        <SummarySkeleton />
      </div>
    );
  }

  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <SummaryCard
        title={t("dashboard.challenges")}
        icon={<Trophy className="h-4 w-4" />}
        active={current?.totalChallengesActive ?? 0}
        upcoming={current?.totalChallengesUpcoming ?? 0}
        completed={current?.totalChallengesCompleted ?? 0}
      />
      <SummaryCard
        title={t("dashboard.events")}
        icon={<CalendarDays className="h-4 w-4" />}
        active={current?.totalEventsActive ?? 0}
        upcoming={current?.totalEventsUpcoming ?? 0}
        completed={current?.totalEventsCompleted ?? 0}
      />
    </div>
  );
}

function SummaryCard({
  title,
  icon,
  active,
  upcoming,
  completed,
}: {
  title: string;
  icon: React.ReactNode;
  active: number;
  upcoming: number;
  completed: number;
}) {
  const { t } = useLocale();
  const total = active + upcoming + completed;

  return (
    <div className="rounded-lg border border-border bg-card p-6">
      <div className="mb-4 flex items-center justify-between">
        <p className="text-sm font-medium text-muted-foreground">{title}</p>
        <div className="text-muted-foreground">{icon}</div>
      </div>
      <div className="grid grid-cols-3 gap-4">
        <StatusColumn
          label={t("dashboard.statusActive")}
          value={active}
          dotColor="bg-emerald-500"
        />
        <StatusColumn
          label={t("dashboard.statusUpcoming")}
          value={upcoming}
          dotColor="bg-blue-500"
        />
        <StatusColumn
          label={t("dashboard.statusCompleted")}
          value={completed}
          dotColor="bg-muted-foreground"
        />
      </div>
      {total > 0 && (
        <div className="mt-4 flex h-1.5 overflow-hidden rounded-full bg-muted">
          {active > 0 && (
            <div
              className="bg-emerald-500 transition-all"
              style={{ width: `${(active / total) * 100}%` }}
            />
          )}
          {upcoming > 0 && (
            <div
              className="bg-blue-500 transition-all"
              style={{ width: `${(upcoming / total) * 100}%` }}
            />
          )}
          {completed > 0 && (
            <div
              className={cn("bg-muted-foreground/40 transition-all")}
              style={{ width: `${(completed / total) * 100}%` }}
            />
          )}
        </div>
      )}
    </div>
  );
}

function StatusColumn({
  label,
  value,
  dotColor,
}: {
  label: string;
  value: number;
  dotColor: string;
}) {
  return (
    <div>
      <div className="flex items-center gap-1.5">
        <div className={cn("h-2 w-2 rounded-full", dotColor)} />
        <span className="text-xs text-muted-foreground">{label}</span>
      </div>
      <p className="mt-1 text-xl font-bold text-foreground">{value}</p>
    </div>
  );
}

function SummarySkeleton() {
  return (
    <div className="rounded-lg border border-border bg-card p-6">
      <div className="h-4 w-20 animate-pulse rounded bg-muted" />
      <div className="mt-4 grid grid-cols-3 gap-4">
        {[1, 2, 3].map((i) => (
          <div key={i}>
            <div className="h-3 w-14 animate-pulse rounded bg-muted" />
            <div className="mt-2 h-6 w-8 animate-pulse rounded bg-muted" />
          </div>
        ))}
      </div>
    </div>
  );
}
